import React, {
  createContext,
  useState,
  useContext,
  ReactNode,
  useEffect,
  useCallback,
} from "react";
import { useNavigate, useLocation } from "react-router-dom";
import authService from "@/services/authService";
import { User } from "@/services/authService";
import { useNotification } from "@/contexts/NotificationContext";
import { useTranslation } from "react-i18next";
import { auth } from "../config/firebase";
import { signInWithCustomToken, sendEmailVerification } from "firebase/auth";

const ADMIN_EMAIL = "vettconsult.web@gmail.com";
const DEBUG_CLIENT_EMAIL = "laura.becr3tive.agency@gmail.com"; // TEMPORAL para debugging
const DEBUG_CLIENT_PRODUCTION = "cliente.debug@vettconsult.com"; // TEMPORAL para debugging en producción

interface AuthContextType {
  user: User | null;
  loading: boolean;
  error: string | null;
  login: (
    email: string,
    password: string,
    recaptchaToken?: string,
  ) => Promise<void>;
  signup: (
    email: string,
    password: string,
    firstName?: string,
    lastName?: string,
    recaptchaToken?: string,
    referralCode?: string,
  ) => Promise<void>;
  logout: () => void;
  loginWithGoogle: (googleToken: string) => Promise<void>;
  loginWithMagicLink: (token: string) => Promise<void>;
  refreshUser: () => Promise<void>;
  clearError: () => void;
  setUser: (user: User | null) => void;
  getDefaultRouteForRole: (role: string) => string;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const useAuth = (): AuthContextType => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error("useAuth must be used within an AuthProvider");
  }
  return context;
};

export const AuthProvider: React.FC<{ children: ReactNode }> = ({
  children,
}) => {
  const [user, setUser] = useState<User | null>(null);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);
  const [isInitialLoad, setIsInitialLoad] = useState<boolean>(true);
  const navigate = useNavigate();
  const location = useLocation();
  const { addNotification } = useNotification();
  const { i18n } = useTranslation();
  const supportedLngs = ["en", "es", "fr"];
  const stripLangFromPath = (path: string): string => {
    const parts = path.split("/");
    if (parts.length > 1 && supportedLngs.includes(parts[1])) {
      const rest = "/" + parts.slice(2).join("/");
      return rest === "/" ? "/" : rest.replace(/\/+$/, "");
    }
    return path;
  };
  const langPrefix = `/${i18n.language}`;

  const isProtectedRoute = useCallback((path: string): boolean => {
    const protectedRoutes = ["/dashboard", "/specialist", "/admin"];
    return protectedRoutes.some((route) => path.startsWith(route));
  }, []);

  const canAccessRoute = useCallback(
    (user: User | null, path: string): boolean => {
      if (!user) return false;

      if (
        user.role === "admin" ||
        user.email === ADMIN_EMAIL ||
        user.email === DEBUG_CLIENT_EMAIL ||
        user.email === DEBUG_CLIENT_PRODUCTION
      ) {
        // Usuario admin tiene acceso completo
        return true;
      }

      if (user.role === "especialista" || user.role === "teacher") {
        // Los especialistas tienen acceso completo a todas las funcionalidades
        return (
          path.startsWith("/specialist") ||
          path.startsWith("/dashboard/ai-chat") ||
          path.startsWith("/dashboard/templates") ||
          path.startsWith("/dashboard/forum") ||
          path.startsWith("/dashboard")
        );
      }

      if (user.role === "cliente" || user.role === "user") {
        return path.startsWith("/dashboard");
      }

      if (user.role === "group_admin") {
        return path.startsWith("/group-admin") || path.startsWith("/dashboard");
      }

      return false;
    },
    [],
  );

  const getDefaultRouteForRole = useCallback((role: string): string => {
    switch (role) {
      case "admin":
        return "/admin";
      case "teacher":
      case "especialista":
        return "/specialist";
      case "group_admin":
        return "/dashboard";
      case "user":
      case "cliente":
        return "/dashboard";
      default:
        return "/dashboard";
    }
  }, []);

  // Cargar usuario al inicializar la aplicación
  useEffect(() => {
    // Solo cargar una vez al inicio
    if (!isInitialLoad) return;

    const loadUser = async () => {
      try {
        // Cargando usuario inicial

        // Verificar si tenemos token antes de hacer la petición
        const token = authService.getToken();
        if (!token) {
          // No hay token, usuario no autenticado
          setUser(null);
          setLoading(false);
          setIsInitialLoad(false);
          return;
        }

        const userData = await authService.getCurrentUser();

        // Usuario cargado exitosamente

        // Solo establecer usuario si tiene datos válidos
        if (userData.uid && userData.email && userData.role !== "none") {
          setUser(userData);
          signIntoFirebaseAuth(); // Fire-and-forget
        } else {
          setUser(null);
        }
      } catch (err: any) {
        console.error("[AUTH_CONTEXT] Error al cargar usuario:", err);

        // ➡️ Fallback: si existe un usuario cacheado, úsalo para evitar redirección forzosa
        const cachedUserStr = localStorage.getItem("user");
        if (cachedUserStr) {
          try {
            const cachedUser = JSON.parse(cachedUserStr);
            if (cachedUser && cachedUser.uid) {
              console.warn(
                "[AUTH_CONTEXT] Usando usuario cacheado como fallback",
              );
              setUser(cachedUser);
            }
          } catch (parseErr) {
            console.error(
              "[AUTH_CONTEXT] No se pudo parsear usuario cacheado:",
              parseErr,
            );
          }
        }

        // Solo manejar errores si no estamos en rutas públicas
        const isPublicRoute = !isProtectedRoute(location.pathname);

        if (!isPublicRoute && err.response?.status !== 404) {
          // Solo hacer logout si es un error de autenticación real, no 404
          if (err.response?.status === 401 || err.response?.status === 403) {
            authService.logout();
            addNotification({
              type: "error",
              message: "Your session has expired. Please login again.",
              autoClose: true,
            });

            if (stripLangFromPath(location.pathname) !== "/login") {
              navigate(`${langPrefix}/login`, {
                state: { from: location },
                replace: true,
              });
            }
          }
        }
        setUser(null);
      } finally {
        setLoading(false);
        setIsInitialLoad(false);
      }
    };

    loadUser();
  }, [isInitialLoad]); // Solo depende de isInitialLoad

  // Verificar acceso a rutas protegidas
  useEffect(() => {
    // No hacer nada si estamos cargando o es la carga inicial
    if (loading || isInitialLoad) return;

    const currentPath = location.pathname;

    // Verificando acceso a ruta protegida

    if (isProtectedRoute(currentPath)) {
      if (!user) {
        // Redirigiendo a login: no hay usuario
        navigate("/login", {
          state: { from: location },
          replace: true,
        });

        addNotification({
          type: "error",
          message: "You must log in to access this page",
          autoClose: true,
        });
      } else if (
        !user.emailVerified &&
        user.email !== ADMIN_EMAIL &&
        user.email !== DEBUG_CLIENT_EMAIL && // TEMPORAL: permitir acceso para debugging
        user.email !== DEBUG_CLIENT_PRODUCTION // TEMPORAL: permitir acceso para debugging en producción
      ) {
        if (currentPath !== "/verify-email") {
          navigate(`${langPrefix}/verify-email`);

          addNotification({
            type: "warning",
            message: "You need to verify your email before continuing",
            autoClose: true,
          });
        }
      } else if (!canAccessRoute(user, currentPath)) {
        // Redirigiendo a ruta por defecto: sin acceso a la ruta actual
        navigate(getDefaultRouteForRole(user.role), { replace: true });

        addNotification({
          type: "error",
          message: "You don't have permission to access this area",
          autoClose: true,
        });
      } else {
        // Acceso permitido a la ruta
      }
    }
  }, [location.pathname, user, loading, isInitialLoad]); // Solo las dependencias mínimas necesarias

  // Sign into Firebase Auth using a server-issued custom token so the Firebase
  // client SDK can access Firestore/Storage with proper security rules.
  const signIntoFirebaseAuth = useCallback(async () => {
    try {
      if (auth.currentUser) return; // already signed in
      const firebaseToken = await authService.getFirebaseToken();
      await signInWithCustomToken(auth, firebaseToken);
    } catch {
      // Non-critical: Firebase Auth sign-in failure doesn't block the app
    }
  }, []);

  const login = async (
    email: string,
    password: string,
    recaptchaToken?: string,
  ) => {
    setLoading(true);
    setError(null);
    try {
      // Iniciando proceso de login
      const { user } = await authService.login({
        email,
        password,
        recaptchaToken,
      });
      // Login exitoso, procesando datos de usuario

      setUser(user);
      signIntoFirebaseAuth(); // Fire-and-forget: sign into Firebase Auth for direct SDK access

      if (
        !user.emailVerified &&
        user.email !== ADMIN_EMAIL &&
        user.email !== DEBUG_CLIENT_EMAIL &&
        user.email !== DEBUG_CLIENT_PRODUCTION
      ) {
        navigate(`${langPrefix}/verify-email`);

        setTimeout(() => {
          setLoading(false);
          addNotification({
            type: "info",
            message: "Please verify your email to continue",
            autoClose: true,
          });
        }, 500);
      } else {
        const from =
          location.state?.from?.pathname || getDefaultRouteForRole(user.role);

        navigate(from);

        setTimeout(() => {
          setLoading(false);
        }, 500);
      }
    } catch (err: any) {
      console.error("[AUTH_CONTEXT] Error en login:", err);
      setError(err.response?.data?.error || err.message || "Error logging in");
      setLoading(false);
      addNotification({
        type: "error",
        message: err.response?.data?.error || err.message || "Error logging in",
        autoClose: true,
      });
    }
  };

  const signup = async (
    email: string,
    password: string,
    firstName?: string,
    lastName?: string,
    recaptchaToken?: string,
    referralCode?: string,
  ) => {
    setLoading(true);
    setError(null);
    try {
      const { firebaseCustomToken } = await authService.signup({
        email,
        password,
        firstName,
        lastName,
        recaptchaToken,
        referralCode,
      });

      // Sign into Firebase client SDK so we can call sendEmailVerification()
      if (firebaseCustomToken) {
        try {
          const cred = await signInWithCustomToken(auth, firebaseCustomToken);
          const appUrl = import.meta.env.VITE_APP_URL || "https://vettconsult.com";
          await sendEmailVerification(cred.user, {
            url: `${appUrl}${langPrefix}/verify-email`,
            handleCodeInApp: false,
          });
        } catch (emailErr: any) {
          // Non-fatal — user can request resend from the verify-email page
          console.warn("[SIGNUP] Could not send verification email:", emailErr?.message);
        }
      }

      navigate(`${langPrefix}/verify-email`);
      addNotification({
        type: "success",
        message: "Registration successful. Please check your email to verify your account.",
        autoClose: true,
      });
    } catch (err: any) {
      console.error("Error en signup:", err);
      setError(
        err.response?.data?.error || err.message || "Error al registrarse",
      );
      addNotification({
        type: "error",
        message:
          err.response?.data?.error ||
          err.message ||
          "Error during registration",
        autoClose: true,
      });
    } finally {
      setLoading(false);
    }
  };

  const logout = () => {
    authService.logout();
    setUser(null);
    setError(null);
  };

  const loginWithGoogle = async (googleToken: string) => {
    setLoading(true);
    setError(null);
    try {
      const { user } = await authService.loginWithGoogle(googleToken);
      setUser(user);
      signIntoFirebaseAuth(); // Fire-and-forget

      const defaultRoute = getDefaultRouteForRole(user.role);
      navigate(defaultRoute);

      // Notificación eliminada para evitar duplicado con MovieIntroLoader
    } catch (err: any) {
      console.error("Error en Google login:", err);

      let errorMessage = "Error al iniciar sesión con Google";

      // Manejar errores específicos
      if (
        err.response?.status === 404 &&
        err.response?.data?.error === "GOOGLE_USER_NOT_REGISTERED"
      ) {
        errorMessage = "Usuario no registrado. Por favor, regístrate primero.";
      } else if (err.response?.data?.message) {
        errorMessage = err.response.data.message;
      } else if (err.response?.data?.error) {
        errorMessage = err.response.data.error;
      } else if (err.message) {
        errorMessage = err.message;
      }

      setError(errorMessage);
      addNotification({
        type: "error",
        message: errorMessage,
        autoClose: true,
      });
    } finally {
      setLoading(false);
    }
  };

  const clearError = () => {
    setError(null);
  };

  const loginWithMagicLink = async (token: string) => {
    setLoading(true);
    setError(null);
    try {
      const { user } = await authService.verifyMagicLink(token);
      setUser(user);
      signIntoFirebaseAuth(); // Fire-and-forget
      const defaultRoute = getDefaultRouteForRole(user.role);
      navigate(defaultRoute);
    } catch (err: any) {
      const errorMessage = err.response?.data?.error || err.message || "Invalid or expired sign-in link";
      setError(errorMessage);
      addNotification({ type: "error", message: errorMessage, autoClose: true });
    } finally {
      setLoading(false);
    }
  };

  const refreshUser = async () => {
    try {
      const userData = await authService.getCurrentUser();

      if (userData.uid && userData.email && userData.role !== "none") {
        setUser(userData);
      }
    } catch (err: any) {
      console.error("[AUTH_CONTEXT] Error al refrescar usuario:", err);
    }
  };

  const value: AuthContextType = {
    user,
    loading,
    error,
    login,
    signup,
    logout,
    loginWithGoogle,
    loginWithMagicLink,
    refreshUser,
    clearError,
    setUser,
    getDefaultRouteForRole,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

export default AuthContext;
